import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister, UseFormWatch } from "react-hook-form";
import { formatCurrency } from "@/common/utils/formatters";
import { CircleAlert } from "lucide-react";
import Select from "@/common/components/Select";
import Accordion from "@/common/components/Accordion";
import Checkbox from "@/common/components/Checkbox";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { CreditProductCategoryConfig } from "./CreditProductCategoryConfig";
import { CompanyBranchValidationSchema } from "../../schemas/companyBranch.schema";
import { CompanyBranchData, CompanyData } from "@/features/company/types/company.type";
import { useClientCompaniesAvailableToAllocateInBranchSummary } from '../../hooks/useClientCompaniesAvailableToAllocateInBranchSummary';
import { ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData } from '../../types/allowedCategoriesAndProducts.type';
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '../../services/allowedCategoriesAndProductsTransformer';
import { Option } from '@/common/types/option';
import useDebounce from "@/common/hooks/useDebounce";
import { filterOptions } from "@/common/constants/filterOptions.constant";
import { FilterOptionOption } from "react-select";

interface CreditConfigurationProps {
  companyData?: CompanyData;
  branchData?: CompanyBranchData;
  register: UseFormRegister<CompanyBranchValidationSchema>;
  control: Control<CompanyBranchValidationSchema>;
  watch: UseFormWatch<CompanyBranchValidationSchema>;
  errors: FieldErrors<CompanyBranchValidationSchema>;
  disableFields?: boolean;
  companyKind?: string
}

export const CreditConfiguration = ({ register, control, watch, errors, companyData, branchData, disableFields, companyKind }: CreditConfigurationProps) => {
  const { fields: categoryGrantedCreditFields, append: appendCategoryGrantedCredit, remove: removeCategoryGrantedCredit } = useFieldArray({
    control,
    name: "product_category_granted_credits_attributes",
  });

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: allowedCategoriesAndProducts,
  } = useClientCompaniesAvailableToAllocateInBranchSummary<ClientCompanyAllowedCategoriesAndProducts>({
    filters: { search: searchProduct },
  });

  const allowedCategoriesOptions = ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({ key: 'categories', watch })(allowedCategoriesAndProducts || {});

  const handleAppendCategory = (option: Option & ClientCompanyAllowedCategoryAndProductData) => {
    if (option.value) {
      appendCategoryGrantedCredit({
        product_category_id: option.value,
        display_name: option.display_name,
        granted_credit: "",
        product_granted_credits_attributes: [],
      });
    }
  };

  const enableCreditConfiguration = watch("enableCreditConfiguration");

  return (
    <div className="flex flex-col gap-4">
      <p className="font-bold text-lg">
        Configurações de crédito
      </p>

      <div className="flex flex-col gap-2">
        <Controller
          name="granted_credit"
          control={control}
          render={({ field }) => (
            <CurrencyTextField
              label="Crédito garantido a filial"
              markAsRequired={!disableFields}
              allowZero={true}
              errorMessage={errors.granted_credit?.message || undefined}
              maxValue={1_000_000_000}
              {...field}
              disabled={disableFields}
            />
          )}
        />

        <div className="flex items-center gap-4">
          <p className="text-[12px] font-light italic">
            {companyData?.kind === 'public_company' ? 'Cota Global do Contrato' : 'Crédito garantido a empresa'}:{' '}
            {formatCurrency(Number(companyData?.granted_credit || 0))}
          </p>

          <p className="text-[12px] font-light italic">
            {companyData?.kind === 'public_company' ? 'Cota livre para alocação' : 'Crédito livre para alocação'}:{' '}
            {formatCurrency(Number(companyData?.unallocated_credit || 0))}
          </p>

          {branchData?.consumed_credit && (
            <p className="text-[12px] font-light italic">
              Crédito consumido:{' '}
              {formatCurrency(Number(branchData?.consumed_credit))}
            </p>
          )}
        </div>
      </div>

      <Accordion
        name="credit_configuration"
        head={(
          <div className="flex flex-col gap-2">
            {errors.enableCreditConfiguration && (
              <p className="text-sm text-danger-soft">
                {errors.enableCreditConfiguration.message || undefined}
              </p>
            )}

            <Checkbox
              label="Crédito garantido por serviço"
              onClick={(event) => event.stopPropagation()}
              {...register("enableCreditConfiguration")}
              disabled={disableFields}
            />
          </div>
        )}
        body={(
          <div className="flex flex-col gap-4">
            <Select
              placeholder="Pesquisar serviços"
              options={allowedCategoriesOptions?.filter((productCategory) => {
                return !categoryGrantedCreditFields?.some(({ product_category_id }) => product_category_id === productCategory.value)
              }) || []}
              onInputChange={setSearchProduct}
              filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
              onChange={(option) => handleAppendCategory(option as Option & ClientCompanyAllowedCategoryAndProductData)}
              value=""
              isDisabled={!enableCreditConfiguration || disableFields}
              name="searchProductCategory"
            />

            <div className="flex items-center gap-2">
              <CircleAlert strokeWidth={1} color="var(--warning)" />

              <div>
                <p className="text-[12px]">
                  Ao selecionar um Serviço e/ou Produto, todos os não selecionados serão bloqueados para consumo
                </p>

                <p className="text-[12px] font-light italic">
                  Certifique-se de adicionar as restrições para todos os serviços e produtos que desejar consumir
                </p>
              </div>
            </div>

            {categoryGrantedCreditFields.map((category, index) => (
              <CreditProductCategoryConfig
                key={index}
                categoryId={category.product_category_id}
                categoryIndex={index}
                removeCategory={() => removeCategoryGrantedCredit(index)}
                control={control}
                errors={errors}
                disabled={!enableCreditConfiguration || disableFields}
                watch={watch}
                companyKind={companyKind}
                controlledBy={companyData?.current_contract?.consumption_controlled_by}
              />
            ))}
          </div>
        )}
      />
    </div>
  );
};
